import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { courseList } from "../../api/Courses";
import {
  AssignSyllabusUpdate,
  FetchSyllabus,
} from "../../api/SyllabusService";

import { getBatchList } from "../../api/BatchService";
import { getSessions } from "../../api/SessionsService";
import { StaffList } from "../../api/Staff";

import { ToastMain, SweetAlertShow } from "../master/Master";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import AcademicHeader from "../Academic/AcademicHeader";

export default function SyllabusAssignUpdate() {
  const location = useLocation();
  const navigate = useNavigate();

  const prefilledData = location.state?.syllabusItem || {};

  // =====================================================
  // STATES
  // =====================================================

  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [batches, setBatches] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [existingTopics, setExistingTopics] = useState([]);

  const [selectedCourse, setSelectedCourse] = useState(
    prefilledData?.course_id ? String(prefilledData.course_id) : ""
  );

  const [selectedSubject, setSelectedSubject] = useState(
    prefilledData?.subject_id ? String(prefilledData.subject_id) : ""
  );

  // Update flow = single batch only (backend does NOT accept an array here)
  const [selectedBatch, setSelectedBatch] = useState(
    prefilledData?.batch_id ? String(prefilledData.batch_id) : ""
  );

  const [selectedStaff, setSelectedStaff] = useState(
    prefilledData?.teacher_id ? String(prefilledData.teacher_id) : ""
  );

  // Update flow = single topic only (backend does NOT accept an array here)
  const [selectedSyllabusId, setSelectedSyllabusId] = useState(
    prefilledData?.syllabus_id ? String(prefilledData.syllabus_id) : ""
  );

  const [sessionId, setSessionId] = useState(
    prefilledData?.session_id ? String(prefilledData.session_id) : ""
  );

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  // =====================================================
  // GET SESSION
  // =====================================================

  const getSession = async () => {
    try {
      const response = await getSessions("&status=1");

      if (response?.data?.status) {
        const sessionData =
          response?.data?.data?.data || response?.data?.data || [];

        if (!sessionId && sessionData.length > 0) {
          const activeSession = sessionData.find(
            (session) => String(session?.status) === "1"
          );

          if (activeSession?.id) {
            setSessionId(String(activeSession.id));
          }
        }
      }
    } catch (error) {
      ToastMain.fire({
        icon: "error",
        title: "Session fetch failed!",
      });
    }
  };

  useEffect(() => {
    getSession();
  }, []);

  // =====================================================
  // COURSE LIST
  // SUBJECT BHI COURSE KE ANDAR SE AAYEGA
  // =====================================================

  const courseFetch = async () => {
    setLoading(true);

    try {
      const response = await courseList();

      if (response?.data?.status) {
        const courseData = response?.data?.data || [];

        setCourses(Array.isArray(courseData) ? courseData : []);

        /*
         * Agar update page par course pehle se selected hai
         * to us course ke subjects direct yahin se set honge.
         */
        if (selectedCourse) {
          const selectedCourseData = courseData.find(
            (course) => String(course?.id) === String(selectedCourse)
          );

          setSubjects(selectedCourseData?.subjects_list || []);
        }
      } else {
        setCourses([]);
        setSubjects([]);
      }
    } catch (error) {
      setCourses([]);
      setSubjects([]);

      ToastMain.fire({
        icon: "error",
        title: "Course fetch failed!",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    courseFetch();
  }, []);

  // =====================================================
  // COURSE CHANGE
  // SUBJECT DIRECT COURSE SE
  // =====================================================

  const handleCourseChange = (e) => {
    const courseId = e.target.value;

    setSelectedCourse(courseId);
    setSelectedSubject("");
    setSelectedBatch("");
    setBatches([]);
    setExistingTopics([]);
    setSelectedSyllabusId("");

    if (!courseId) {
      setSubjects([]);
      return;
    }

    const selectedCourseData = courses.find(
      (course) => String(course?.id) === String(courseId)
    );

    /*
     * Course response ke andar se subjects_list
     */
    setSubjects(selectedCourseData?.subjects_list || []);
  };

  // =====================================================
  // BATCH LIST
  // SESSION + COURSE BASED
  // =====================================================

  const batchList = async () => {
    if (!sessionId || !selectedCourse) {
      setBatches([]);
      return;
    }

    setLoading(true);

    try {
      const response = await getBatchList(
        `&status=1&session_id=${sessionId}&course_id=${selectedCourse}`
      );

      if (response?.data?.status) {
        const batchData =
          response?.data?.batches || response?.data?.data || [];

        setBatches(Array.isArray(batchData) ? batchData : []);
      } else {
        setBatches([]);
      }
    } catch (error) {
      setBatches([]);

      ToastMain.fire({
        icon: "error",
        title: "Batch fetch failed!",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId && selectedCourse) {
      batchList();
    }
  }, [sessionId, selectedCourse]);

  // =====================================================
  // STAFF LIST
  // =====================================================

  const getStaffs = async () => {
    setLoading(true);

    try {
      const response = await StaffList("&type=Teaching");

      if (response?.data?.status) {
        const staffData =
          response?.data?.data?.data || response?.data?.data || [];

        setStaffs(Array.isArray(staffData) ? staffData : []);
      } else {
        setStaffs([]);
      }
    } catch (error) {
      setStaffs([]);

      ToastMain.fire({
        icon: "error",
        title: "Staff fetch failed!",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStaffs();
  }, []);

  // =====================================================
  // SYLLABUS LIST
  // COURSE + SUBJECT BASED
  // =====================================================

  const syllabusFetch = async () => {
    if (!selectedCourse || !selectedSubject) {
      setExistingTopics([]);
      return;
    }

    setLoading(true);

    try {
      let addUrl =
        `&course_id=${selectedCourse}` + `&subject_id=${selectedSubject}`;

      if (search) {
        addUrl += `&search=${encodeURIComponent(search)}`;
      }

      const response = await FetchSyllabus(addUrl);

      if (response?.data?.status) {
        const list = response?.data?.data?.data || [];

        const existing = Array.isArray(list)
          ? list.map((item) => ({
            id: String(item?.id),
            topic: item?.title || "",
            description: item?.description || "",
            duration: item?.duration_day || 0,
          }))
          : [];

        setExistingTopics(existing);

        /*
         * Existing assigned syllabus ko selected rakho (single).
         */
        if (prefilledData?.syllabus_id) {
          const stillExists = existing.some(
            (item) => String(item.id) === String(prefilledData.syllabus_id)
          );

          setSelectedSyllabusId(
            stillExists ? String(prefilledData.syllabus_id) : ""
          );
        }
      } else {
        setExistingTopics([]);
      }
    } catch (error) {
      setExistingTopics([]);

      ToastMain.fire({
        icon: "error",
        title: "Syllabus fetch failed!",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCourse && selectedSubject) {
      syllabusFetch();
    }
  }, [selectedCourse, selectedSubject]);

  // =====================================================
  // BATCH SELECT (single)
  // =====================================================

  const handleBatchSelect = (batchId) => {
    setSelectedBatch((previous) => (previous === batchId ? "" : batchId));
  };

  // =====================================================
  // TOPIC SELECT (single)
  // =====================================================

  const handleTopicSelect = (id) => {
    const stringId = String(id);
    setSelectedSyllabusId((previous) =>
      previous === stringId ? "" : stringId
    );
  };

  // =====================================================
  // SUBMIT UPDATE
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCourse) {
      ToastMain.fire({ icon: "info", title: "Please select course!" });
      return;
    }

    if (!selectedSubject) {
      ToastMain.fire({ icon: "info", title: "Please select subject!" });
      return;
    }

    if (!selectedStaff) {
      ToastMain.fire({ icon: "info", title: "Please select staff!" });
      return;
    }

    if (!selectedBatch) {
      ToastMain.fire({ icon: "info", title: "Please select batch!" });
      return;
    }

    if (!selectedSyllabusId) {
      ToastMain.fire({
        icon: "info",
        title: "Please select syllabus topic!",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("id", prefilledData?.id || "");
      formData.append("session_id", sessionId || "");
      formData.append("course_id", selectedCourse);
      formData.append("subject_id", selectedSubject);
      formData.append("teacher_id", selectedStaff);

      // Single values only — update endpoint expects scalars, not arrays
      formData.append("batch_id", selectedBatch);
      formData.append("syllabus_id", selectedSyllabusId);

      const response = await AssignSyllabusUpdate(formData);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message || "Syllabus updated successfully!"
        );

        navigate(-1);
      } else {
        SweetAlertShow(
          "error",
          "Error",
          response?.data?.message || "Something went wrong!"
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong!"
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedBatchName =
    batches.find((batch) => String(batch?.id) === selectedBatch)?.title ||
    "";

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading && (
        <div
          className="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 9999, background: "rgba(255,255,255,0.6)" }}
        >
          <div className="sk-chase sk-primary display-1">
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
          </div>
        </div>
      )}

      {/* =====================================================
          LAYOUT
      ===================================================== */}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid mt-2">
                <div className="row">
                  <AcademicHeader />

                  <div className="col-md-12">
                    <div className="card shadow-sm border-0">
                      {/* =================================================
                          HEADER
                      ================================================= */}

                      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 bg-label-primary d-flex align-items-center justify-content-center"
                            style={{ width: 44, height: 44 }}
                          >
                            <i className="ri-edit-2-line fs-4" />
                          </div>
                          <div>
                            <h4 className="text-primary m-0">
                              Update Syllabus Assign
                            </h4>
                            <small className="text-muted">
                              Change batch, teacher or topic for this
                              assignment
                            </small>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => navigate(-1)}
                          disabled={loading}
                        >
                          <i className="ri-arrow-left-line me-1" />
                          Back
                        </button>
                      </div>

                      {/* =================================================
                          BODY
                      ================================================= */}

                      <div className="card-body">
                        <form onSubmit={handleSubmit}>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span
                              className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: 24, height: 24, fontSize: 12 }}
                            >
                              1
                            </span>
                            <h6 className="mb-0">Assignment Details</h6>
                          </div>

                          <div className="row g-2 mb-2">
                            {/* COURSE */}
                            <div className="col-md-4">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={selectedCourse}
                                  onChange={handleCourseChange}
                                  required
                                >
                                  <option value="">Select Course</option>
                                  {courses?.map((course) => (
                                    <option key={course?.id} value={course?.id}>
                                      {course?.title}
                                    </option>
                                  ))}
                                </select>
                                <label>Select Course</label>
                              </div>
                            </div>

                            {/* SUBJECT */}
                            <div className="col-md-4">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={selectedSubject}
                                  onChange={(e) => {
                                    setSelectedSubject(e.target.value);
                                    setExistingTopics([]);
                                    setSelectedSyllabusId("");
                                  }}
                                  required
                                  disabled={!selectedCourse}
                                >
                                  <option value="">Select Subject</option>
                                  {subjects?.map((subject) => (
                                    <option
                                      key={subject?.id}
                                      value={subject?.id}
                                    >
                                      {subject?.name}
                                    </option>
                                  ))}
                                </select>
                                <label>Select Subject</label>
                              </div>
                            </div>

                            {/* STAFF */}
                            <div className="col-md-4">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={selectedStaff}
                                  onChange={(e) =>
                                    setSelectedStaff(e.target.value)
                                  }
                                  required
                                >
                                  <option value="">Select Staff</option>
                                  {staffs?.map((staff) => (
                                    <option key={staff?.id} value={staff?.id}>
                                      {staff?.name}
                                    </option>
                                  ))}
                                </select>
                                <label>Select Staff</label>
                              </div>
                            </div>
                          </div>

                          {/* BATCH (single select as chips) */}
                          <div className="mb-4">
                            <label className="form-label small text-muted mb-2">
                              Batch <span className="text-danger">*</span>{" "}
                              <span className="fst-italic">
                                (select one)
                              </span>
                            </label>

                            {!selectedCourse ? (
                              <div className="text-muted small">
                                Select a course to see available batches.
                              </div>
                            ) : batches.length === 0 ? (
                              <div className="text-muted small">
                                No active batch found for this course.
                              </div>
                            ) : (
                              <div className="d-flex flex-wrap gap-2">
                                {batches.map((batch) => {
                                  const isSelected =
                                    selectedBatch === String(batch?.id);

                                  return (
                                    <button
                                      key={batch?.id}
                                      type="button"
                                      className={`btn btn-sm ${isSelected
                                        ? "btn-primary"
                                        : "btn-outline-secondary"
                                        }`}
                                      onClick={() =>
                                        handleBatchSelect(String(batch?.id))
                                      }
                                    >
                                      {isSelected && (
                                        <i className="ri-check-line me-1" />
                                      )}
                                      {batch?.title}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* =================================================
                              EXISTING TOPICS
                          ================================================= */}

                          {selectedCourse && selectedSubject && (
                            <>
                              <hr />

                              <div className="d-flex align-items-center gap-2 mb-3">
                                <span
                                  className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                  style={{
                                    width: 24,
                                    height: 24,
                                    fontSize: 12,
                                  }}
                                >
                                  2
                                </span>
                                <h6 className="mb-0">Select Topic</h6>
                                <span className="text-muted small fst-italic">
                                  (select one)
                                </span>
                              </div>
                            </>
                          )}

                          {existingTopics.map((row) => {
                            const isChecked =
                              selectedSyllabusId === String(row?.id);

                            return (
                              <div
                                key={row?.id}
                                className={`d-flex align-items-start gap-3 p-3 mb-2 rounded-3 border ${isChecked
                                  ? "border-primary bg-label-primary bg-opacity-10"
                                  : ""
                                  }`}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleTopicSelect(row?.id)}
                              >
                                <input
                                  type="radio"
                                  name="syllabusTopic"
                                  className="form-check-input mt-1 flex-shrink-0"
                                  style={{ width: 20, height: 20 }}
                                  id={`topic-${row?.id}`}
                                  checked={isChecked}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() =>
                                    handleTopicSelect(row?.id)
                                  }
                                />

                                <label
                                  htmlFor={`topic-${row?.id}`}
                                  className="flex-grow-1"
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <span className="fw-semibold text-body">
                                      {row?.topic || "Untitled topic"}
                                    </span>

                                    <span className="badge bg-label-info">
                                      <i className="ri-time-line me-1" />
                                      {row?.duration || 0} days
                                    </span>
                                  </div>

                                  {row?.description && (
                                    <div className="text-muted small mt-1">
                                      {row.description}
                                    </div>
                                  )}
                                </label>
                              </div>
                            );
                          })}

                          {/* =================================================
                              NO TOPICS
                          ================================================= */}

                          {selectedCourse &&
                            selectedSubject &&
                            existingTopics.length === 0 && (
                              <div className="d-flex flex-column align-items-center justify-content-center text-center border rounded-3 py-4 bg-light bg-opacity-50">
                                <i className="ri-file-list-3-line fs-2 text-muted mb-2" />
                                <span className="text-muted small">
                                  No syllabus topics available for selected
                                  subject.
                                </span>
                              </div>
                            )}

                          {/* =================================================
                              SUMMARY + BUTTONS
                          ================================================= */}

                          <div className="d-flex flex-wrap justify-content-between align-items-center mt-4 pt-3 border-top">
                            <span className="text-muted small">
                              {selectedSyllabusId ? (
                                <>
                                  Updating topic for{" "}
                                  <strong>
                                    {selectedBatchName || "selected batch"}
                                  </strong>
                                </>
                              ) : (
                                "Select a topic above to continue"
                              )}
                            </span>

                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate(-1)}
                                disabled={loading}
                              >
                                Cancel
                              </button>

                              <button
                                type="submit"
                                className="btn btn-primary px-4"
                                disabled={loading}
                              >
                                {loading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    />
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-save-line me-1" />
                                    Update Syllabus
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}